import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Table,
} from 'reactstrap';
import * as firebaseService from '../../services/firebase';
import { logger } from '../../logging';
import moment from 'moment';
import { ref } from '../../utils/firebase';
import {
  equalTo,
  orderByChild,
  query,
  get,
  ref as fbRef,
  getDatabase,
  update,
} from 'firebase/database';
import { buildCommunityLeaderMappingUpdate } from './communityLeaderMapping';
const R = require('ramda');

const addressbookStyles = require('./addressbook.css');

export const AddressbookMappingModal = ({
  addressbook,
  mappingModalIsOpen,
  setMappingModalIsOpen,
  setMappingAddressbook,
  tableDataUpdated,
  mappingAddressbookIndex,
}) => {
  const ifbApp = firebaseService.getIFBApp();
  const ifbDatabase = getDatabase(ifbApp);

  const initialLeaderId = addressbook.communityLeaderId || '';
  const [saveIsDisabled, setSaveIsDisabled] = useState(true);
  const [communityLeaderId, setCommunityLeaderId] = useState(initialLeaderId);
  const [communityLeaderName, setCommunityLeaderName] = useState(
    addressbook.communityLeaderName || ''
  );
  const [availableLeadersSorted, setAvailableLeadersSorted] = useState([]);
  const [availableLeaders, setAvailableLeaders] = useState({});

  const leaderOptions = R.map((leader) => {
    return (
      <option value={leader.id} key={leader.id}>
        {leader.name}
      </option>
    );
  }, availableLeadersSorted);

  const updateSaveDisabled = (nextLeaderId: string) => {
    setSaveIsDisabled(nextLeaderId === initialLeaderId);
  };

  useEffect(() => {
    const leaderId = addressbook.communityLeaderId || '';
    setCommunityLeaderId(leaderId);
    setCommunityLeaderName(addressbook.communityLeaderName || '');
    setSaveIsDisabled(true);
    getAvailableLeaders();
  }, [addressbook]);

  const getAvailableLeaders = async () => {
    if (addressbook.partyIdValid !== true) {
      return;
    }

    try {
      const leadersRef = ref('community/leaders');
      const leadersQuery = query(
        leadersRef,
        orderByChild('party_id'),
        equalTo(addressbook.party_id)
      );
      const leadersSnap = await get(leadersQuery);
      const leaders = {};
      leadersSnap.forEach((v) => {
        const leader = v.val();
        if (addressbook.party_id === leader.party_id) {
          leaders[leader.id] = {
            id: leader.id,
            name: leader.name,
            party_id: leader.party_id,
            party_name: leader.party_name,
          };
        }
      });

      const sortedLeaders = R.sort((a, b) => {
        if (a.name > b.name) {
          return 1;
        }
        if (a.name < b.name) {
          return -1;
        }
        return 0;
      }, R.values(leaders));

      setAvailableLeadersSorted(sortedLeaders);
      setAvailableLeaders(leaders);
      logger.debug(`setAvailableLeaders: ${JSON.stringify(leaders)}`);
    } catch (e) {
      logger.error(`getAvailableLeaders: ${e}`);
      setAvailableLeadersSorted([]);
      setAvailableLeaders({});
    }
  };

  const saveAddressbookMapping = async () => {
    logger.debug(
      `saveAddressbookMapping called for addressbook: ${addressbook.key}`
    );
    const now = moment().format('YYYY-MM-DDTHH:mm:ssZ');
    const addressbooksRef = fbRef(
      ifbDatabase,
      `addressbooks/${addressbook.key}`
    );
    const mappingUpdate = buildCommunityLeaderMappingUpdate({
      partyIdValid: addressbook.partyIdValid,
      addressbookKey: addressbook.key,
      communityLeaderId: communityLeaderId || null,
      communityLeaderName: communityLeaderName || null,
      modified: now,
    });

    try {
      await update(addressbooksRef, mappingUpdate);
      tableDataUpdated(
        mappingAddressbookIndex,
        'communityLeaderId',
        mappingUpdate.communityLeaderId
      );
      tableDataUpdated(
        mappingAddressbookIndex,
        'communityLeaderName',
        mappingUpdate.communityLeaderName
      );
      tableDataUpdated(
        mappingAddressbookIndex,
        'communityLeaderMappingValid',
        mappingUpdate.communityLeaderMappingValid
      );
      setMappingAddressbook({});
      setMappingModalIsOpen(false);
    } catch (e) {
      logger.error('[updateMyData] - error updating addressbook: ', e);
      setMappingAddressbook({});
      setMappingModalIsOpen(false);
    }
  };

  const leaderIdChanged = (e) => {
    const nextLeaderId = e.target.value;
    setCommunityLeaderId(nextLeaderId);
    const leaderName =
      (availableLeaders[nextLeaderId] && availableLeaders[nextLeaderId].name) ||
      '';
    setCommunityLeaderName(leaderName);
    updateSaveDisabled(nextLeaderId);
  };

  const removeMapping = () => {
    setCommunityLeaderId('');
    setCommunityLeaderName('');
    updateSaveDisabled('');
  };

  const handleClose = () => {
    setMappingAddressbook({});
    setMappingModalIsOpen(false);
    setSaveIsDisabled(true);
    setAvailableLeadersSorted([]);
    setAvailableLeaders({});
    setCommunityLeaderId('');
    setCommunityLeaderName('');
  };

  return (
    <Modal
      isOpen={mappingModalIsOpen}
      toggle={handleClose}
      className={addressbookStyles.mappingModal}
    >
      <ModalHeader
        toggle={handleClose}
        className={addressbookStyles.mappingModalHeader}
      >
        Community Leader Mapping for Addressbook: '{addressbook.name}' (
        {addressbook.accountName})
      </ModalHeader>
      <ModalBody className={addressbookStyles.mappingModalBody}>
        {availableLeadersSorted.length === 0 ? (
          <Row className={addressbookStyles.mappingModalBodyRow}>
            <Col>
              <h5>
                There are no Community Leaders which match the criteria to map
                to this addressbook
              </h5>
            </Col>
          </Row>
        ) : null}
        <Row className={addressbookStyles.mappingModalBodyRow}>
          <Col>
            <h6>Community Leaders must match the following criteria:</h6>
            <ul>
              <li>
                Community Leader must match Addressbook party id (
                {addressbook.party_id}
                {addressbook.party_name ? ` - ${addressbook.party_name}` : ''})
              </li>
            </ul>
          </Col>
        </Row>
        {availableLeadersSorted.length > 0 ? (
          <React.Fragment>
            <Row className={addressbookStyles.mappingModalBodyRow}>
              <Col>
                1) Select a community leader, change the current mapping, or
                choose &quot;Please select&quot; / Remove mapping to clear it
              </Col>
            </Row>
            <Row className={addressbookStyles.mappingModalBodyRow}>
              <Col>
                <Table>
                  <thead>
                    <tr>
                      <th>Leader Name</th>
                      <th>Leader ID</th>
                      <th>Party ID</th>
                      <th>Party Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Input
                          type="select"
                          value={communityLeaderId || ''}
                          onChange={leaderIdChanged}
                        >
                          <option value="" key="">
                            Please select
                          </option>
                          {leaderOptions}
                        </Input>
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={communityLeaderId || ''}
                          disabled={true}
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={
                            (availableLeaders[communityLeaderId] &&
                              availableLeaders[communityLeaderId].party_id) ||
                            ''
                          }
                          disabled={true}
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={
                            (availableLeaders[communityLeaderId] &&
                              availableLeaders[communityLeaderId].party_name) ||
                            ''
                          }
                          disabled={true}
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
            <Row className={addressbookStyles.mappingModalFooterRow}>
              <Col>
                2) Save to commit the mapping change and schedule for push to
                Big Query
              </Col>
            </Row>
            <Row className={addressbookStyles.mappingModalFooterRow}>
              <Col>
                <Button
                  color="primary"
                  onClick={() => saveAddressbookMapping()}
                  disabled={saveIsDisabled}
                >
                  Save
                </Button>
                {initialLeaderId !== '' ? (
                  <Button
                    color="secondary"
                    className="ms-2"
                    onClick={removeMapping}
                    disabled={communityLeaderId === '' && saveIsDisabled}
                  >
                    Remove mapping
                  </Button>
                ) : null}
              </Col>
            </Row>
          </React.Fragment>
        ) : null}
      </ModalBody>
    </Modal>
  );
};
